import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SurveyState {
  currentPage: string;
  fileData: any[] | null;
  workshopColumn: string | null;
  axes: Record<string, string[]>;
  scaleMapping: Record<string, number>; // Global default scale
  questionScaleMapping: Record<string, Record<string, number>>; // Per-question scales
  analysisResults: any | null;
  detectedResponses: string[] | null; // Unique responses found in Excel
  questionResponses: Record<string, string[]> | null; // Responses per question
  setCurrentPage: (page: string) => void;
  setFileData: (data: any[]) => void;
  setWorkshopColumn: (column: string) => void;
  setAxes: (axes: Record<string, string[]>) => void;
  addAxis: (name: string) => void;
  removeAxis: (name: string) => void;
  addQuestionsToAxis: (axisName: string, questions: string[]) => void;
  removeQuestionFromAxis: (axisName: string, question: string) => void;
  setScaleMapping: (mapping: Record<string, number>) => void;
  setQuestionScaleMapping: (questionName: string, mapping: Record<string, number>) => void;
  removeQuestionScaleMapping: (questionName: string) => void;
  setDetectedResponses: (responses: string[]) => void;
  setQuestionResponses: (responses: Record<string, string[]>) => void;
  setAnalysisResults: (results: any) => void;
  clearAllData: () => void;
  hasFile: () => boolean;
  hasAxes: () => boolean;
  hasScaleMapping: () => boolean;
  hasAnalysisResults: () => boolean;
  canProceedToAnalysis: () => boolean;
}

const initialState = {
  currentPage: 'file_upload',
  fileData: null,
  workshopColumn: null,
  axes: {},
  scaleMapping: {},
  questionScaleMapping: {},
  analysisResults: null,
  detectedResponses: null,
  questionResponses: null,
};

export const useStore = create<SurveyState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentPage: (page) => set({ currentPage: page }),
      setFileData: (data) => set({ fileData: data }),
      setWorkshopColumn: (column) => set({ workshopColumn: column }),
      setAxes: (axes) => set({ axes }),

      addAxis: (name) => {
        if (!name.trim()) return;
        const currentAxes = get().axes;
        if (currentAxes[name]) return;
        set({ axes: { ...currentAxes, [name]: [] } });
      },

      removeAxis: (name) => {
        const { [name]: removed, ...remainingAxes } = get().axes;
        set({ axes: remainingAxes });
      },

      addQuestionsToAxis: (axisName, questions) => {
        const currentAxes = get().axes;
        if (!currentAxes[axisName]) return;

        const updatedQuestions = [...new Set([...currentAxes[axisName], ...questions])];
        set({
          axes: {
            ...currentAxes,
            [axisName]: updatedQuestions
          }
        });
      },

      removeQuestionFromAxis: (axisName, question) => {
        const currentAxes = get().axes;
        if (!currentAxes[axisName]) return;

        set({
          axes: {
            ...currentAxes,
            [axisName]: currentAxes[axisName].filter(q => q !== question)
          }
        });
      },

      setScaleMapping: (mapping) => set({ scaleMapping: mapping }),
      setQuestionScaleMapping: (questionName, mapping) => {
        const current = get().questionScaleMapping;
        set({ questionScaleMapping: { ...current, [questionName]: mapping } });
      },
      removeQuestionScaleMapping: (questionName) => {
        const { [questionName]: _, ...remaining } = get().questionScaleMapping;
        set({ questionScaleMapping: remaining });
      },
      setDetectedResponses: (responses) => set({ detectedResponses: responses }),
      setQuestionResponses: (responses) => set({ questionResponses: responses }),
      setAnalysisResults: (results) => set({ analysisResults: results }),
      
      clearAllData: () => set(initialState),

      hasFile: () => {
        const state = get();
        return Boolean(state.fileData?.length > 0 && state.workshopColumn);
      },

      hasAxes: () => {
        const axes = get().axes;
        return Boolean(
          Object.keys(axes).length > 0 && 
          Object.values(axes).some(questions => questions.length > 0)
        );
      },

      hasScaleMapping: () => {
        const mapping = get().scaleMapping;
        return Boolean(Object.keys(mapping).length > 0);
      },

      hasAnalysisResults: () => Boolean(get().analysisResults),
      
      canProceedToAnalysis: () => {
        const state = get();
        return Boolean(
          state.fileData?.length > 0 && 
          state.workshopColumn &&
          Object.keys(state.axes).length > 0 &&
          Object.values(state.axes).some(questions => questions.length > 0) &&
          Object.keys(state.scaleMapping).length > 0
        );
      }
    }),
    {
      name: 'survey-storage',
      // Save everything except functions
    }
  )
);